'use client';

import React, { useMemo, useState } from 'react';
import type { StoredTrade } from '@/app/utils/tradeStorage';

interface CorrelationMatrixProps {
  trades: StoredTrade[];
}

function pearsonCorrelation(a: number[], b: number[]): number {
  const n = a.length;
  if (n < 2) return 0;
  const meanA = a.reduce((s, v) => s + v, 0) / n;
  const meanB = b.reduce((s, v) => s + v, 0) / n;
  let num = 0, denomA = 0, denomB = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - meanA;
    const db = b[i] - meanB;
    num += da * db;
    denomA += da * da;
    denomB += db * db;
  }
  const denom = Math.sqrt(denomA * denomB);
  return denom === 0 ? 0 : num / denom;
}

function correlationColor(r: number): string {
  // r in [-1, 1]
  if (r >= 0.7) return 'bg-emerald-500/80 text-white';
  if (r >= 0.4) return 'bg-emerald-500/40 text-emerald-300';
  if (r >= 0.1) return 'bg-emerald-500/15 text-emerald-400/70';
  if (r >= -0.1) return 'bg-muted/40 text-muted-foreground';
  if (r >= -0.4) return 'bg-red-500/15 text-red-400/70';
  if (r >= -0.7) return 'bg-red-500/40 text-red-300';
  return 'bg-red-500/80 text-white';
}

export default function CorrelationMatrix({ trades }: CorrelationMatrixProps) {
  const [hoveredCell, setHoveredCell] = useState<{ r: number; c: number } | null>(null);

  const { symbols, matrix } = useMemo(() => {
    // Group daily P&L by symbol
    const symbolMap: Record<string, Record<string, number>> = {};
    trades.forEach((t) => {
      const sym = t.symbol?.toUpperCase();
      if (!sym) return;
      const day = t.dateISO?.slice(0, 10) ?? t.date;
      if (!symbolMap[sym]) symbolMap[sym] = {};
      symbolMap[sym][day] = (symbolMap[sym][day] ?? 0) + t.pnl;
    });

    // Only keep symbols with ≥3 trades
    const syms = Object.entries(symbolMap)
      .filter(([, days]) => Object.keys(days).length >= 3)
      .sort((a, b) => Object.keys(b[1]).length - Object.keys(a[1]).length)
      .slice(0, 10)
      .map(([sym]) => sym);

    if (syms.length < 2) return { symbols: syms, matrix: [] };

    // Collect all unique dates
    const allDates = Array.from(
      new Set(syms.flatMap((s) => Object.keys(symbolMap[s])))
    ).sort();

    // Build vectors aligned by date
    const vectors: Record<string, number[]> = {};
    syms.forEach((s) => {
      vectors[s] = allDates.map((d) => symbolMap[s][d] ?? 0);
    });

    // Build correlation matrix
    const mat: number[][] = syms.map((a) =>
      syms.map((b) => (a === b ? 1 : pearsonCorrelation(vectors[a], vectors[b])))
    );

    return { symbols: syms, matrix: mat };
  }, [trades]);

  if (symbols.length < 2) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground">Symbol Correlation Matrix</h3>
          <p className="text-xs text-muted-foreground mt-0.5">P&L correlation between traded symbols</p>
        </div>
        <div className="h-40 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Need trades across ≥2 symbols to compute correlations</p>
        </div>
      </div>
    );
  }

  const hovered = hoveredCell ? matrix[hoveredCell.r][hoveredCell.c] : null;
  const hoveredA = hoveredCell ? symbols[hoveredCell.r] : null;
  const hoveredB = hoveredCell ? symbols[hoveredCell.c] : null;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Symbol Correlation Matrix</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Daily P&L correlation — green = co-move, red = inverse</p>
        </div>
        {hoveredCell && hovered !== null && hoveredA !== hoveredB && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">{hoveredA} / {hoveredB}</p>
            <p className={`text-sm font-mono font-semibold ${hovered >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              r = {hovered.toFixed(2)}
            </p>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="border-collapse text-xs" style={{ minWidth: symbols.length * 44 + 56 }}>
          <thead>
            <tr>
              <th className="w-14" />
              {symbols.map((s) => (
                <th key={`col-${s}`} className="w-11 pb-1.5 text-center font-500 text-muted-foreground whitespace-nowrap">
                  <span className="block truncate max-w-[40px]" title={s}>{s.length > 5 ? s.slice(0, 4) + '…' : s}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {symbols.map((rowSym, ri) => (
              <tr key={`row-${rowSym}`}>
                <td className="pr-2 py-0.5 text-right text-muted-foreground font-500 whitespace-nowrap w-14">
                  <span className="truncate block max-w-[52px]" title={rowSym}>{rowSym.length > 6 ? rowSym.slice(0, 5) + '…' : rowSym}</span>
                </td>
                {symbols.map((colSym, ci) => {
                  const val = matrix[ri][ci];
                  const isHovered = hoveredCell?.r === ri && hoveredCell?.c === ci;
                  return (
                    <td key={`cell-${rowSym}-${colSym}`} className="p-0.5">
                      <div
                        className={`w-10 h-10 rounded-md flex items-center justify-center cursor-default transition-all font-mono font-semibold text-[10px] ${correlationColor(val)} ${isHovered ? 'ring-2 ring-white/30 scale-105' : ''}`}
                        onMouseEnter={() => setHoveredCell({ r: ri, c: ci })}
                        onMouseLeave={() => setHoveredCell(null)}
                        title={`${rowSym} / ${colSym}: r = ${val.toFixed(2)}`}
                      >
                        {ri === ci ? '—' : val.toFixed(2)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border flex-wrap">
        <span className="text-xs text-muted-foreground">Correlation:</span>
        {[
          { label: 'Strong +', cls: 'bg-emerald-500/80' },
          { label: 'Moderate +', cls: 'bg-emerald-500/40' },
          { label: 'Neutral', cls: 'bg-muted/40' },
          { label: 'Moderate −', cls: 'bg-red-500/40' },
          { label: 'Strong −', cls: 'bg-red-500/80' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded-sm ${l.cls}`} />
            <span className="text-xs text-muted-foreground">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
